import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { api_endpoint } from "@/constants/constant";
import { ClipLoader } from "react-spinners";

function normalizeRoleId(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export default function BatchDeleteRoles({
  open,
  onOpenChange,
  selectedRoles,
  onDeleted,
  refetchRoles,
}) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open) return;
    setConfirmText("");
  }, [open]);

  const normalizedRoleIds = useMemo(() => {
    const rows = Array.isArray(selectedRoles) ? selectedRoles : [];
    const ids = rows
      .map((r) => normalizeRoleId(r?.id ?? r?.role_id ?? r?.roleId))
      .filter((v) => v != null);
    return Array.from(new Set(ids));
  }, [selectedRoles]);

  const roleCount = normalizedRoleIds.length;
  const canSubmit =
    roleCount >= 2 && String(confirmText || "").trim().toUpperCase() === "DELETE";

  const handleDelete = async () => {
    if (!canSubmit) {
      if (roleCount < 2) {
        toast.error("Select at least two roles.");
      } else {
        toast.error('Type "DELETE" to confirm.');
      }
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const payload = {
        role_ids: normalizedRoleIds,
      };

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      };

      const url = `${api_endpoint}/user/users/roles/bulk-delete`;

      try {
        await axios.delete(url, reqConfig);
      } catch (err) {
        const status = err?.response?.status;
        // Some backends implement this as POST.
        if (status === 405) {
          await axios.post(url, payload, {
            headers: reqConfig.headers,
          });
        } else {
          throw err;
        }
      }

      toast.success(
        `Deleted ${roleCount} role${roleCount === 1 ? "" : "s"} successfully!`
      );

      onOpenChange(false);
      onDeleted?.(payload);
      // Defer so parent refetch isn't affected by dialog state batching.
      if (refetchRoles) setTimeout(() => refetchRoles(), 0);
    } catch (error) {
      console.error("Error batch-deleting roles:", error);
      toast.error("Failed to delete roles.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Delete Roles
          </DialogTitle>
        </DialogHeader>

        <div className="">
          <p className="text-sm text-(--neutral-900) mb-4 font-body">
            Are you sure you want to delete{" "}
            <span className="font-extrabold text-(--neutral-900) mr-1">
              {roleCount} role{roleCount === 1 ? "" : "s"}
            </span>
            ? This action cannot be undone.
          </p>

          <p className="text-(--color-primary) font-bold mt-2">
            Warning: Deleting roles will also affect all users assigned to them.
            <br />
            This may remove their access. Please ensure you have re-assigned or
            informed affected users before proceeding.
          </p>

          <div className="mt-4">
            <label className="font-body text-sm text-(--neutral-900)">
              Type <b>DELETE</b> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="border rounded px-2 py-1 w-full mt-1 text-(--neutral-700)"
              placeholder='Type "DELETE"'
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 justify-between">
          <DialogClose asChild>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
          </DialogClose>
          <Button variant="" onClick={handleDelete} disabled={loading || !canSubmit}>
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
